/**
 * Mock data for product service
 */

import {
  Product,
  ProductCategory,
  ProductStatus,
  ProductImage,
  ProductSpecification,
  ProductInventory,
  ProductDimensions,
  ProductVendor,
} from "./product.types";

export const mockProductVendor: ProductVendor = {
  id: "vendor-1",
  name: "Tech Solutions Inc.",
  email: "vendor@techsolutions.com",
  phone: "+1-555-0123",
  address: "123 Tech Street, Silicon Valley, CA 94000",
};

export const mockProductCategory: ProductCategory = {
  id: "cat-1",
  name: "Electronics",
  slug: "electronics",
  description: "Electronic devices and components",
  isActive: true,
};

// Placeholder image as data URI (no external request needed)
const MOCK_PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23e5e7eb' width='400' height='300'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='18' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";

export const mockProductImages: ProductImage[] = [
  {
    id: "img-1",
    url: MOCK_PLACEHOLDER_IMAGE,
    alt: "Product main image",
    isPrimary: true,
    order: 1,
  },
  {
    id: "img-2",
    url: MOCK_PLACEHOLDER_IMAGE,
    alt: "Product side view",
    isPrimary: false,
    order: 2,
  },
];

export const mockProductSpecifications: ProductSpecification[] = [
  { name: "Color", value: "Black", unit: "" },
  { name: "Weight", value: "1.5", unit: "kg" },
  { name: "Dimensions", value: "30x20x10", unit: "cm" },
  { name: "Material", value: "Aluminum", unit: "" },
];

export const mockProductInventory: ProductInventory = {
  quantity: 100,
  reserved: 5,
  available: 95,
  lowStockThreshold: 10,
  trackInventory: true,
};

export const mockProductDimensions: ProductDimensions = {
  length: 30,
  width: 20,
  height: 10,
  unit: "cm",
};

export const mockProduct: Product = {
  id: "prod-1",
  name: "Wireless Bluetooth Headphones",
  description:
    "High-quality wireless headphones with noise cancellation and 30-hour battery life.",
  sku: "WBH-001",
  price: 199.99,
  cost: 120.0,
  category: mockProductCategory,
  subcategory: "Audio",
  brand: "TechSound",
  images: mockProductImages,
  specifications: mockProductSpecifications,
  inventory: mockProductInventory,
  status: ProductStatus.ACTIVE,
  tags: ["wireless", "bluetooth", "headphones", "audio"],
  isActive: true,
  isFeatured: true,
  weight: 1.5,
  dimensions: mockProductDimensions,
  vendor: mockProductVendor,
  createdAt: "2024-01-01T00:00:00Z",
  updatedAt: "2024-01-15T10:30:00Z",
  publishedAt: "2024-01-02T00:00:00Z",
};

export const mockProducts: Product[] = [
  mockProduct,
  {
    id: "prod-2",
    name: "Smart Watch Series 5",
    description: "Advanced smartwatch with health monitoring and GPS tracking.",
    sku: "SWS-005",
    price: 299.99,
    cost: 180.0,
    category: mockProductCategory,
    subcategory: "Wearables",
    brand: "SmartTech",
    images: [
      {
        id: "img-3",
        url: MOCK_PLACEHOLDER_IMAGE,
        alt: "Smart watch front view",
        isPrimary: true,
        order: 1,
      },
    ],
    specifications: [
      { name: "Display", value: "1.4 inch", unit: "AMOLED" },
      { name: "Battery", value: "7 days", unit: "life" },
      { name: "Water Resistance", value: "50m", unit: "" },
    ],
    inventory: {
      quantity: 50,
      reserved: 2,
      available: 48,
      lowStockThreshold: 5,
      trackInventory: true,
    },
    status: ProductStatus.ACTIVE,
    tags: ["smartwatch", "fitness", "gps", "health"],
    isActive: true,
    isFeatured: false,
    weight: 0.3,
    dimensions: {
      length: 4,
      width: 4,
      height: 1,
      unit: "cm",
    },
    vendor: mockProductVendor,
    createdAt: "2024-01-02T00:00:00Z",
    updatedAt: "2024-01-14T15:45:00Z",
    publishedAt: "2024-01-03T00:00:00Z",
  },
  {
    id: "prod-3",
    name: "Mechanical Gaming Keyboard",
    description:
      "RGB mechanical keyboard with customizable keys and macro support.",
    sku: "MGK-003",
    price: 149.99,
    cost: 90.0,
    category: mockProductCategory,
    subcategory: "Gaming",
    brand: "GameGear",
    images: [
      {
        id: "img-4",
        url: MOCK_PLACEHOLDER_IMAGE,
        alt: "Gaming keyboard",
        isPrimary: true,
        order: 1,
      },
    ],
    specifications: [
      { name: "Switch Type", value: "Cherry MX Blue", unit: "" },
      { name: "Backlight", value: "RGB", unit: "" },
      { name: "Connectivity", value: "USB-C", unit: "" },
    ],
    inventory: {
      quantity: 75,
      reserved: 8,
      available: 67,
      lowStockThreshold: 10,
      trackInventory: true,
    },
    status: ProductStatus.ACTIVE,
    tags: ["gaming", "keyboard", "mechanical", "rgb"],
    isActive: true,
    isFeatured: true,
    weight: 1.2,
    dimensions: {
      length: 45,
      width: 15,
      height: 3,
      unit: "cm",
    },
    vendor: mockProductVendor,
    createdAt: "2024-01-03T00:00:00Z",
    updatedAt: "2024-01-13T09:20:00Z",
    publishedAt: "2024-01-04T00:00:00Z",
  },
];

export const mockProductCategories: ProductCategory[] = [
  mockProductCategory,
  {
    id: "cat-2",
    name: "Clothing",
    slug: "clothing",
    description: "Apparel and fashion items",
    isActive: true,
  },
  {
    id: "cat-3",
    name: "Home & Garden",
    slug: "home-garden",
    description: "Home improvement and garden supplies",
    isActive: true,
  },
];

export const mockProductResponse = {
  product: mockProduct,
  message: "Product retrieved successfully",
  success: true,
};

export const mockProductsResponse = {
  products: mockProducts,
  pagination: {
    page: 1,
    limit: 10,
    total: 3,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  },
  message: "Products retrieved successfully",
  success: true,
};

export const mockProductError = {
  message: "Product not found",
  success: false,
  status: 404,
  code: "PRODUCT_NOT_FOUND",
};
